import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { SidebarLayout } from '../components/layout';
import { Button } from '../components/ui/button';
import { Tabs } from '../components/ui/tabs';
import { Input, Checkbox, Select } from '../components/ui/form';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Modal } from '../components/ui/modal';
import { WorkflowStepsEditor } from '../components/workflow/workflow-steps-editor';
import { WorkflowMatchIdentifierManager } from '../components/workflow/workflow-match-identifier-manager';
import { WorkflowAuditTrail } from '../components/workflow/workflow-audit-trail';
import { useDivisionWorkflow, useUpdateDivisionWorkflow } from '../hooks/use-division-workflows';
import { useDivision } from '../hooks/use-divisions';
import { useWorkflowOrderAttributes, useDeleteOrderAttribute } from '../hooks/use-order-attributes';
import { useOrders } from '../hooks/use-orders';
import { UpdateWorkflowRequest, CreateWorkflowStepDto, RoutingRuleDto } from '../types';
import { formatCurrency, formatDate } from '../utils';
import { useAuth } from '../hooks/use-auth';

export function WorkflowEditPage() {
  const { divisionId, workflowId } = useParams<{ divisionId: string; workflowId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'basic');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [duplicateRuleIds, setDuplicateRuleIds] = useState<Set<string>>(new Set());
  // Track whether the form has been populated from the API so that cache
  // updates after save don't re-initialize the form (which doubled the steps).
  const formPopulatedRef = useRef(false);
  
  // Fetch the specific workflow directly by ID (detail endpoint includes all steps)
  const { data: workflow, isLoading } = useDivisionWorkflow(divisionId as string, workflowId as string);
  const updateWorkflow = useUpdateDivisionWorkflow(divisionId as string);
  const { data: divisionData } = useDivision(divisionId || '', !!divisionId);
  
  // Fetch order attributes for this workflow
  const { data: orderAttributesResponse, isLoading: isLoadingFields } = useWorkflowOrderAttributes(workflowId as string);
  const deleteFieldMutation = useDeleteOrderAttribute(workflowId as string);
  
  // Initialize orders filter state from URL params
  const [ordersFilters, setOrdersFilters] = useState(() => ({
    page: parseInt(searchParams.get('orderPage') || '1'),
    limit: parseInt(searchParams.get('orderLimit') || '50'),
    search: searchParams.get('orderSearch') || '',
    status: searchParams.get('orderStatus') || '',
    sortBy: searchParams.get('orderSortBy') || 'CreatedAt',
    sortOrder: (searchParams.get('orderSortOrder') || 'desc') as 'asc' | 'desc'
  }));
  
  // Debounced search for orders
  const [ordersSearchInput, setOrdersSearchInput] = useState(ordersFilters.search);
  useEffect(() => {
    const timer = setTimeout(() => {
      setOrdersFilters(prev => ({ ...prev, search: ordersSearchInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [ordersSearchInput]);
  
  // Sync orders filters to URL params when on orders tab
  useEffect(() => {
    if (activeTab === 'orders') {
      const params = new URLSearchParams(searchParams);
      params.set('tab', 'orders');
      params.set('orderPage', ordersFilters.page.toString());
      params.set('orderLimit', ordersFilters.limit.toString());
      if (ordersFilters.search) {
        params.set('orderSearch', ordersFilters.search);
      } else {
        params.delete('orderSearch');
      }
      if (ordersFilters.status) {
        params.set('orderStatus', ordersFilters.status);
      } else {
        params.delete('orderStatus');
      }
      params.set('orderSortBy', ordersFilters.sortBy);
      params.set('orderSortOrder', ordersFilters.sortOrder);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, ordersFilters, setSearchParams]);
  
  // Fetch orders for this workflow
  const { data: ordersResponse, isLoading: isLoadingOrders } = useOrders(
    divisionId as string, 
    { 
      workflowId: workflowId,
      page: ordersFilters.page,
      limit: ordersFilters.limit,
      search: ordersFilters.search || undefined,
      status: ordersFilters.status || undefined,
      sortBy: ordersFilters.sortBy,
      sortOrder: ordersFilters.sortOrder
    }
  );
  
  // Order attributes state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<UpdateWorkflowRequest>({
    name: '',
    description: '',
    matchingMode: 'none',
    matchCriteria: {
      matchIdentifiers: [],
      executionIdentifiers: [],
    },
    workflowSteps: [],
    routingRules: [],
    active: true,
    settings: {
      autoEscalation: true,
      escalationHours: 24,
      notifications: {
        email: true,
        slack: false,
        sms: false,
      },
      timeoutAction: 'escalate',
    },
  });
  
  // Update active tab when query parameter changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (workflow && !formPopulatedRef.current) {
      formPopulatedRef.current = true;
      const loadedRules = workflow.routingRules || [];
      // Derive matchingMode from what's actually configured, correcting stale 'none' values
      let derivedMatchingMode = workflow.matchingMode || 'none';
      if (loadedRules.length > 0 && derivedMatchingMode === 'none') {
        derivedMatchingMode = 'routingRules';
      }
      setFormData({
        name: workflow.name,
        description: workflow.description || '',
        matchingMode: derivedMatchingMode,
        matchCriteria: workflow.matchCriteria || {
          matchIdentifiers: [],
          executionIdentifiers: [],
        },
        workflowSteps: workflow.steps || [],
        routingRules: loadedRules,
        active: workflow.active,
        settings: workflow.settings,
      });
    }
  }, [workflow]);

  const handleSave = async () => {
    if (!divisionId || !workflowId) return;
    
    try {
      setErrorMessage(null);
      await updateWorkflow.mutateAsync({
        workflowId,
        request: formData,
      });
      setSuccessMessage('Workflow saved successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (error: any) {
      console.error('Failed to update workflow:', error);
      // Extract error message from API response
      let message = 'Failed to update workflow';
      if (error.response?.data) {
        // ASP.NET Core error format
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.title) {
          message = error.response.data.detail || error.response.data.title;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else {
          message = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        message = error.message;
      }
      setErrorMessage(message);
      
      // Parse error message to find duplicate rules
      if (message.includes('Routing rule') && message.includes('already exists')) {
        const duplicates = new Set<string>();
        // Extract the rule details from error message like: "Routing rule '$.ShipVia equals Will Call' already exists"
        const match = message.match(/Routing rule '([^']+)'/);
        if (match && match[1]) {
          const [field, operator, ...valueParts] = match[1].split(' ');
          const value = valueParts.join(' ');
          
          // Find matching rule(s) in current form data
          formData.routingRules?.forEach(rule => {
            const ruleField = rule.field;
            const ruleValue = rule.value;
            const ruleOperator = rule.operator;
            
            if (ruleField?.toLowerCase().trim() === field?.toLowerCase().trim() &&
                ruleOperator?.toLowerCase().replace('-', '').trim() === operator?.toLowerCase().replace('-', '').trim() &&
                ruleValue?.toLowerCase().trim() === value?.toLowerCase().trim()) {
              duplicates.add(rule.id);
            }
          });
        }
        setDuplicateRuleIds(duplicates);
      } else {
        setDuplicateRuleIds(new Set());
      }
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = () => {
    navigate(`/workflows/${divisionId}`);
  };

  // Handle tab change with URL update
  const handleTabChange = (tabId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    
    // Only include order filter params if switching to orders tab
    if (tabId !== 'orders') {
      params.delete('orderPage');
      params.delete('orderLimit');
      params.delete('orderSearch');
      params.delete('orderStatus');
      params.delete('orderSortBy');
      params.delete('orderSortOrder');
    }
    
    setSearchParams(params, { replace: true });
    setActiveTab(tabId);
  };

  // Orders grid helper functions
  const handleOrdersSort = (field: string) => {
    setOrdersFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const getOrdersSortIcon = (field: string) => {
    if (ordersFilters.sortBy !== field) {
      return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    }
    return ordersFilters.sortOrder === 'asc' 
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'info';
      case 'inapproval': 
      case 'in approval': return 'warning';
      case 'approved': 
      case 'completed': return 'success';
      case 'rejected': 
      case 'cancelled': return 'error';
      case 'paused': return 'warning';
      case 'needsresolved': 
      case 'needs resolved': return 'error';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, str => str.toUpperCase());
  };

  const handleWorkflowStepsChange = useCallback((steps: CreateWorkflowStepDto[]) => {
    setFormData(prev => ({
      ...prev,
      workflowSteps: steps,
    }));
  }, []);

  const handleRoutingRulesChange = useCallback((rules: RoutingRuleDto[]) => {
    setFormData(prev => ({
      ...prev,
      routingRules: rules,
      // Automatically keep matchingMode in sync: if rules exist it must be 'routingRules',
      // if all rules are removed fall back to 'none' so it doesn't silently stay stuck.
      matchingMode: rules.length > 0 ? 'routingRules' : 'none',
    }));
  }, []);

  const handleDeleteField = (fieldId: string) => {
    setFieldToDelete(fieldId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!fieldToDelete) return;

    try {
      await deleteFieldMutation.mutateAsync(fieldToDelete);
      setDeleteConfirmOpen(false);
      setFieldToDelete(null);
    } catch (error) {
      console.error('Failed to delete editable field:', error);
    }
  };

  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      content: (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">
              Workflow Name
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter workflow name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter workflow description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Workflow Type
            </label>
            <div className="text-sm text-secondary-700 dark:text-secondary-200 bg-secondary-50 dark:bg-secondary-900 px-3 py-2 rounded-md border border-secondary-200 dark:border-secondary-700">
              {workflow?.type || 'N/A'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Matching Mode
            </label>
            <select
              value={formData.matchingMode || 'none'}
              onChange={(e) => setFormData({ ...formData, matchingMode: e.target.value as any })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="none">None — manual selection only</option>
              <option value="routingRules">Routing Rules — matched by routing rule conditions</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Controls how this workflow is automatically selected when processing orders.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Default Step Timeout (hours)
            </label>
            <Input
              type="number"
              min="1"
              value={formData.settings?.defaultStepTimeoutHours?.toString() ?? ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                settings: {
                  ...formData.settings,
                  defaultStepTimeoutHours: e.target.value ? parseInt(e.target.value) : undefined
                } as any
              })}
              placeholder="Leave empty to use division default"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Override the division's default step timeout for this workflow. Leave empty to use the division default.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Pause Timeout (hours)
            </label>
            <Input
              type="number"
              min="1"
              value={formData.settings?.pauseTimeoutHours?.toString() ?? ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                settings: {
                  ...formData.settings,
                  pauseTimeoutHours: e.target.value ? parseInt(e.target.value) : undefined
                } as any
              })}
              placeholder="Leave empty to use division default"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Override the division's default pause timeout for this workflow. Leave empty to use the division default.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Default Timeout Warning (hours before)
            </label>
            <Input
              type="number"
              min="1"
              value={formData.settings?.defaultTimeoutWarningHours?.toString() ?? ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                settings: {
                  ...formData.settings,
                  defaultTimeoutWarningHours: e.target.value ? parseInt(e.target.value) : undefined
                } as any
              })}
              placeholder="Leave empty to use division default"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Override the division's default timeout warning for this workflow. Leave empty to use the division default.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={(divisionData?.settings?.requireExplicitSubmit ?? false) || (formData.settings?.requireExplicitSubmit ?? false)}
              onChange={(e) => setFormData({
                ...formData,
                settings: {
                  ...formData.settings,
                  requireExplicitSubmit: e.target.checked
                } as any
              })}
              disabled={divisionData?.settings?.requireExplicitSubmit ?? false}
            />
            <label className="text-sm font-medium">
              Require explicit "Submit" at every step (overrides each step's own setting)
              {(divisionData?.settings?.requireExplicitSubmit ?? false) && (
                <span className="italic text-gray-500 dark:text-secondary-400"> - enforced by division setting</span>
              )}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
            <label className="text-sm font-medium">Active</label>
          </div>
        </div>
      ),
    },
    {
      id: 'workflow-steps',
      label: 'Workflow Steps',
      content: (
        <WorkflowStepsEditor
          divisionId={divisionId as string}
          workflowSteps={formData.workflowSteps || []}
          onChange={handleWorkflowStepsChange}
          forceRequireExplicitSubmit={(divisionData?.settings?.requireExplicitSubmit ?? false) || (formData.settings?.requireExplicitSubmit ?? false)}
        />
      ),
    },
    {
      id: 'routing-rules',
      label: 'Routing Rules',
      content: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Order Routing Rules</h3>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
                Define conditions to match orders to this workflow
              </p>
            </div>
            <Button
              onClick={() => {
                const newRule: RoutingRuleDto = {
                  id: crypto.randomUUID(),
                  field: '',
                  operator: 'equals',
                  value: '',
                };
                handleRoutingRulesChange([...(formData.routingRules || []), newRule]);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>

          {!formData.routingRules || formData.routingRules.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
              No routing rules configured. Click "Add Rule" to create one.
            </div>
          ) : (
            <div className="space-y-4">
              {formData.routingRules.map((rule, index) => {
                const isDuplicate = duplicateRuleIds.has(rule.id);
                
                return (
                  <div 
                    key={rule.id} 
                    className={`border rounded-lg p-4 ${
                      isDuplicate 
                        ? 'border-red-500 bg-red-50 dark:bg-red-950 ring-2 ring-red-200' 
                        : 'border-gray-200 dark:border-secondary-700'
                    }`}
                  >
                    {isDuplicate && (
                      <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-semibold mb-3">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Duplicate rule - This rule already exists in another workflow
                      </div>
                    )}
                  <div className="flex items-start gap-4">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Field Path
                        </label>
                        <Input
                          value={rule.field}
                          onChange={(e) => {
                            const updated = [...(formData.routingRules || [])];
                            updated[index] = { ...rule, field: e.target.value };
                            handleRoutingRulesChange(updated);
                          }}
                          placeholder="e.g., $.Order_Attributes[?(@.Name == 'DoDAAC')].Value"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Operator
                        </label>
                        <select
                          value={rule.operator}
                          onChange={(e) => {
                            const updated = [...(formData.routingRules || [])];
                            updated[index] = { ...rule, operator: e.target.value };
                            handleRoutingRulesChange(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        >
                          <option value="equals">Equals</option>
                          <option value="contains">Contains</option>
                          <option value="startsWith">Starts With</option>
                          <option value="endsWith">Ends With</option>
                          <option value="greaterThan">Greater Than</option>
                          <option value="lessThan">Less Than</option>
                          <option value="between">Between</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Value
                        </label>
                        <Input
                          value={rule.value}
                          onChange={(e) => {
                            const updated = [...(formData.routingRules || [])];
                            updated[index] = { ...rule, value: e.target.value };
                            handleRoutingRulesChange(updated);
                          }}
                          placeholder="Value to match"
                        />
                      </div>
                      {rule.operator === 'between' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            End Value
                          </label>
                          <Input
                            value={rule.valueEnd || ''}
                            onChange={(e) => {
                              const updated = [...(formData.routingRules || [])];
                              updated[index] = { ...rule, valueEnd: e.target.value };
                              handleRoutingRulesChange(updated);
                            }}
                            placeholder="End value"
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = (formData.routingRules || []).filter((_, i) => i !== index);
                        handleRoutingRulesChange(updated);
                      }}
                      className="mt-7"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'order-attributes',
      label: 'Order Attributes',
      content: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Order Attributes</h3>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
                Configure order fields that can be edited during approval
              </p>
            </div>
            <Button
              onClick={() => navigate(`/workflows/${divisionId}/${workflowId}/order-attributes/new`)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Attribute
            </Button>
          </div>

          {isLoadingFields ? (
            <div className="text-center py-8">Loading order attributes...</div>
          ) : !orderAttributesResponse?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
              No order attributes configured. Click "Add Attribute" to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field Label</TableHead>
                  <TableHead>JSON Path</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Validation</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderAttributesResponse.map((field: any) => (
                  <TableRow key={field.id}>
                    <TableCell className="font-medium">{field.label}</TableCell>
                    <TableCell className="font-mono text-sm">{field.jsonPath}</TableCell>
                    <TableCell>
                      <Badge variant="info">{field.fieldType}</Badge>
                    </TableCell>
                    <TableCell>
                      {field.isRequired ? (
                        <Badge variant="default">Required</Badge>
                      ) : (
                        <Badge variant="info">Optional</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {field.validationRules ? (
                        <Badge variant="info">Has Rules</Badge>
                      ) : (
                        <span className="text-gray-400 dark:text-secondary-500">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/workflows/${divisionId}/${workflowId}/order-attributes/${field.id}`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteField(field.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ),
    },
    {
      id: 'orders',
      label: 'Orders',
      content: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Orders Using This Workflow</h3>
              <p className="text-sm text-gray-600 dark:text-secondary-400 mt-1">
                All orders that have been processed through this workflow
              </p>
            </div>
            <Badge variant="info">
              {ordersResponse?.totalCount || 0} total
            </Badge>
          </div>

          {/* Search and Filters */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-4">
            <div className="space-y-4">
              {/* Search Bar */}
              <div>
                <input
                  type="text"
                  placeholder="Search by order number..."
                  value={ordersSearchInput}
                  onChange={(e) => setOrdersSearchInput(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
              </div>

              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Status
                  </label>
                  <select
                    value={ordersFilters.status}
                    onChange={(e) => setOrdersFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="">All Statuses</option>
                    <option value="Submitted">Submitted</option>
                    <option value="InApproval">In Approval</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Paused">Paused</option>
                    <option value="NeedsResolved">Needs Resolved</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Sort By
                  </label>
                  <select
                    value={ordersFilters.sortBy}
                    onChange={(e) => setOrdersFilters(prev => ({ ...prev, sortBy: e.target.value, page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="CreatedAt">Created Date</option>
                    <option value="UpdatedAt">Updated Date</option>
                    <option value="OrderNumber">Order Number</option>
                    <option value="TotalAmount">Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Sort Order
                  </label>
                  <select
                    value={ordersFilters.sortOrder}
                    onChange={(e) => setOrdersFilters(prev => ({ ...prev, sortOrder: e.target.value as 'asc' | 'desc', page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Page Size
                  </label>
                  <select
                    value={ordersFilters.limit}
                    onChange={(e) => setOrdersFilters(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {isLoadingOrders ? (
            <div className="text-center py-8">Loading orders...</div>
          ) : !ordersResponse?.items?.length ? (
            <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
              {ordersFilters.search || ordersFilters.status 
                ? 'No orders found matching your filters.' 
                : 'No orders have been processed through this workflow yet.'}
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                    <thead className="bg-gray-50 dark:bg-secondary-900">
                      <tr>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          onClick={() => handleOrdersSort('OrderNumber')}
                        >
                          Order Number
                          {getOrdersSortIcon('OrderNumber')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Submitter
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          onClick={() => handleOrdersSort('TotalAmount')}
                        >
                          Total
                          {getOrdersSortIcon('TotalAmount')}
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          onClick={() => handleOrdersSort('CreatedAt')}
                        >
                          Created
                          {getOrdersSortIcon('CreatedAt')}
                        </th>
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          onClick={() => handleOrdersSort('UpdatedAt')}
                        >
                          Updated
                          {getOrdersSortIcon('UpdatedAt')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                      {ordersResponse.items.map((order: any) => (
                        <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-secondary-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                              onClick={() => navigate(`/order-detail/${order.id}`)}
                              title="Click to view order details"
                            >
                              {order.orderNumber}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{order.submitterName || 'Unknown'}</div>
                            {order.submitterEmail && (
                              <div className="text-sm text-gray-500 dark:text-secondary-400">{order.submitterEmail}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge 
                              variant={getStatusBadgeVariant(order.status)}
                            >
                              {formatStatus(order.status)}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {order.originalTotalAmount && order.originalTotalAmount !== order.totalAmount ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <span className="line-through text-gray-400 dark:text-secondary-500 text-xs">
                                  {formatCurrency(order.originalTotalAmount, order.currency)}
                                </span>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                  {formatCurrency(order.totalAmount, order.currency)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-900 dark:text-white">
                                {formatCurrency(order.totalAmount, order.currency)}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-secondary-400">
                            {formatDate(order.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-secondary-400">
                            {formatDate(order.updatedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/order-detail/${order.id}`)}
                              title="View Order Details"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {ordersResponse.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-secondary-200">
                    Showing {((ordersResponse.page - 1) * ordersResponse.pageSize) + 1} to {Math.min(ordersResponse.page * ordersResponse.pageSize, ordersResponse.totalCount)} of {ordersResponse.totalCount} results
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setOrdersFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={ordersResponse.page === 1}
                      variant="outline"
                      size="sm"
                    >
                      <i className="fa-solid fa-chevron-left mr-2"></i>
                      Previous
                    </Button>
                    
                    <div className="flex items-center space-x-2">
                      {Array.from({ length: Math.min(5, ordersResponse.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (ordersResponse.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (ordersResponse.page <= 3) {
                          pageNum = i + 1;
                        } else if (ordersResponse.page >= ordersResponse.totalPages - 2) {
                          pageNum = ordersResponse.totalPages - 4 + i;
                        } else {
                          pageNum = ordersResponse.page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            onClick={() => setOrdersFilters(prev => ({ ...prev, page: pageNum }))}
                            variant={ordersResponse.page === pageNum ? 'primary' : 'outline'}
                            size="sm"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      onClick={() => setOrdersFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={ordersResponse.page === ordersResponse.totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <i className="fa-solid fa-chevron-right ml-2"></i>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      id: 'audit',
      label: 'Audit History',
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Workflow Change History</h3>
            <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">
              Complete audit trail of all changes made to this workflow
            </p>
          </div>
          <WorkflowAuditTrail 
            divisionId={divisionId as string} 
            workflowId={workflowId as string}
          />
        </div>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-red-600 dark:text-red-400">Please log in to edit workflows.</div>
        </div>
      </SidebarLayout>
    );
  }

  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading workflow...</div>
        </div>
      </SidebarLayout>
    );
  }

  if (!workflow) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg text-red-600 dark:text-red-400">Workflow not found</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="p-6">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workflows
          </Button>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Edit Workflow</h1>
              <p className="text-gray-600 dark:text-secondary-400 mt-1">{workflow.name}</p>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateWorkflow.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {updateWorkflow.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {workflow.isDeleted && (
          <div role="alert" className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.593c.75 1.334-.213 2.985-1.742 2.985H3.48c-1.53 0-2.492-1.651-1.743-2.985L8.257 3.1zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">This workflow has been deleted</p>
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  {workflow.deletedAt
                    ? `It was deleted on ${formatDate(workflow.deletedAt)} and can no longer be assigned to new orders. It's still viewable/editable here because it may still be referenced by in-progress orders.`
                    : "It can no longer be assigned to new orders. It's still viewable/editable here because it may still be referenced by in-progress orders."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success Toast */}
        {successMessage && (
          <div role="status" className="mb-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-900">{successMessage}</p>
              </div>
              <button
                onClick={() => setSuccessMessage(null)}
                className="flex-shrink-0 text-green-600 dark:text-green-400 hover:text-green-800"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error Message Display */}
        {errorMessage && (
          <div role="alert" className="mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">Error Updating Workflow</h3>
                <p className="text-sm text-red-800">
                  {errorMessage.includes('already exists in workflow') ? (
                    <span>
                      {errorMessage.split('already exists in workflow')[0]}
                      <span className="font-semibold">already exists in workflow</span>
                      {errorMessage.split('already exists in workflow')[1]}
                    </span>
                  ) : (
                    errorMessage
                  )}
                </p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <Tabs
          tabs={tabs}
          activeTabId={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Editable Field"
      >
        <div className="space-y-4">
          <p>Are you sure you want to delete this editable field? This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              disabled={deleteFieldMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteFieldMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </SidebarLayout>
  );
}
